import { Component, OnInit, Input } from '@angular/core';
import { AbstractControl, Validators } from "@angular/forms";
import { ApplicationSteps } from "../../../../models/applications.models";
import { IUploadFile } from '../../../../../common/services/document-management.service';
import { ApplicationSectionBaseComponent } from "../../../application-section-base/application-section-base.component";
import { AuthService } from "../../../../../services/auth.service";
import { OnBaseDocumentTypeProcessReference, OnBaseSubProcessName } from '../../../../../common/models/onBaseIntegrationAndMapping.model';

@Component({
    selector: 'app-documentation',
    templateUrl: './documentation.component.html',
    styleUrls: ['./documentation.component.scss', '../../../../../core/components/collapsible/collapsible.component.scss', '../../../application-section-base/application-section-base.component.scss'],
    standalone: false
})
export class DocumentationComponent extends ApplicationSectionBaseComponent implements OnInit {

    @Input() onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];

    readonly step: ApplicationSteps = ApplicationSteps.Documentation;

    get aerialPhotographyCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get("aerialPhotography");
    }

    get aerialPhotographyFiles(): IUploadFile[] {
        if (this.aerialPhotographyCtrl == null) return [];
        return <IUploadFile[]>this.aerialPhotographyCtrl.value;
    }

    get aerialPhotographyWithContoursCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get("aerialPhotographyWithContours");
    }

    get aerialPhotographyWithContoursFiles(): IUploadFile[] {
        if (this.aerialPhotographyWithContoursCtrl == null) return [];
        return <IUploadFile[]>this.aerialPhotographyWithContoursCtrl.value;
    }

    get miscellaneousCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get("miscellaneousDocuments");
    }

    get miscellaneousFiles(): IUploadFile[] {
        if (this.miscellaneousCtrl == null) return [];
        return <IUploadFile[]>this.miscellaneousCtrl.value;
    }

    get platCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get("plat");
    }

    get platFiles(): IUploadFile[] {
        if (this.platCtrl == null) return [];
        return <IUploadFile[]>this.platCtrl.value;
    }

    get restorationCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get("restorationPlan");
    }

    get restorationFiles(): IUploadFile[] {
        if (this.restorationCtrl == null) return [];
        return <IUploadFile[]>this.restorationCtrl.value;
    }

    get urbanAreaCtrl(): AbstractControl {
        if (this.form == null) return null;;
        return <AbstractControl>this.form.get("urbanAreaSwornStatementAffidavit");
    }

    get urbanAreaFiles(): IUploadFile[] {
        if (this.urbanAreaCtrl == null) return [];
        return <IUploadFile[]>this.urbanAreaCtrl.value;
    }

    constructor(_auth: AuthService) { super(_auth); }

    ngOnInit() {
    }

    aerialPhotographyFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.aerialPhotographyFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(f => f.subProcessName == OnBaseSubProcessName.AerialPhotographyForUrbanArea)?.onBaseDocumentTypeName;
            files.push(evt);
            this.aerialPhotographyCtrl.setValue(files);
        }
    }

    aerialPhotographyFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.aerialPhotographyFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.aerialPhotographyCtrl.setValue(files);
        }
    }

    aerialPhotographyContoursFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.aerialPhotographyWithContoursFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(f => f.subProcessName == OnBaseSubProcessName.AerialPhotographyWithContours)?.onBaseDocumentTypeName;
            files.push(evt);
            this.aerialPhotographyWithContoursCtrl.setValue(files);
        }
    }

    aerialPhotographyContoursFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.aerialPhotographyWithContoursFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.aerialPhotographyWithContoursCtrl.setValue(files);
        }
    }

    miscellaneousFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.miscellaneousFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(f => f.subProcessName == OnBaseSubProcessName.AdditionalPertinentDocuments)?.onBaseDocumentTypeName;
            files.push(evt);
            this.miscellaneousCtrl.setValue(files);
        }
    }

    miscellaneousFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.miscellaneousFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.miscellaneousCtrl.setValue(files);
        }
    }

    restorationFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.restorationFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(f => f.subProcessName == OnBaseSubProcessName.RestorationPlan)?.onBaseDocumentTypeName;
            files.push(evt);
            this.restorationCtrl.setValue(files);
        }
    }

    restorationFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.restorationFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.restorationCtrl.setValue(files);
        }
    }

    platFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.platFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(f => f.subProcessName == OnBaseSubProcessName.PlatMapsPermitted)?.onBaseDocumentTypeName;
            files.push(evt);
            this.platCtrl.setValue(files);
        }
    }

    platFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.platFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.platCtrl.setValue(files);
        }
    }


    toggleOpen(): void {
        if (this.step == this.activeStep || !this.isOpen) super.toggleOpen();
        this.openChanged.emit(this.step);
    }

    urbanAreaFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.urbanAreaFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(f => f.subProcessName == OnBaseSubProcessName.UrbanAreaAffidavit)?.onBaseDocumentTypeName;
            files.push(evt);
            this.urbanAreaCtrl.setValue(files);
        }
    }

    urbanAreaFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.urbanAreaFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.urbanAreaCtrl.setValue(files);
        }
    }

    urbanizedAreaChanged(value: string): void {
        if (this.aerialPhotographyCtrl != null && this.aerialPhotographyWithContoursCtrl != null && this.urbanAreaCtrl != null) {
            if (value != "NONURB") {
                this.aerialPhotographyCtrl.setValidators([Validators.required, Validators.minLength(1)]);
                this.aerialPhotographyWithContoursCtrl.setValidators([Validators.required, Validators.minLength(1)]);
                this.urbanAreaCtrl.setValidators([Validators.required, Validators.minLength(1)]);
            } else {
                this.aerialPhotographyCtrl.clearValidators();
                this.aerialPhotographyWithContoursCtrl.clearValidators();
                this.urbanAreaCtrl.clearValidators();
            }
            this.aerialPhotographyCtrl.updateValueAndValidity();
            this.aerialPhotographyWithContoursCtrl.updateValueAndValidity();
            this.urbanAreaCtrl.updateValueAndValidity();
        }
    }
}
